import { MagnifyingGlass as Search, FileText as FileCheck, ShieldCheck, Car, CreditCard, Handshake } from '@phosphor-icons/react';
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/lib/router-compat";

import { SEOHreflang } from "@/components/seo/SEOHreflang";
import { useTranslation } from "@/hooks/useTranslation";
import { useCountry } from "@/hooks/useCountry";
import { pageContent } from "@/utils/translations";
import { TestimonialCTA } from "@/components/home/TestimonialCTA";

const HowItWorks = () => {
  const { language, t } = useTranslation();
  const { country } = useCountry();
  const content = pageContent[language];
  
  const basePath = country === 'UK' ? '/uk' : country === 'DE' ? '/de' : '';
  const baseUrl = 'https://roautomarket.ro';
  const canonicalPath = country === 'RO' ? '/cum-functioneaza' : `${basePath}/how-it-works`;
  const canonicalUrl = `${baseUrl}${canonicalPath}`;
  
  const steps = [
    {
      icon: Search,
      title: t('how_step1_title'),
      description: t('how_step1_desc'),
      details: [
        t('categories.cars'),
        t('filters.apply'),
        t('save_search.button'),
        t('actions.compare')
      ]
    },
    {
      icon: FileCheck,
      title: t('how_step2_title'),
      description: t('how_step2_desc'),
      details: [
        t('listing.location'),
        t('listing.description'),
        t('listing.mileage'),
        t('listing.condition')
      ]
    },
    {
      icon: ShieldCheck,
      title: t('how_step3_title'),
      description: t('how_step3_desc'),
      details: [
        t('filters.seller_type'),
        t('support.response_time'),
        t('listing.contact'),
        t('common.from') + ' ' + t('filters.seller_type')
      ]
    },
    {
      icon: Car,
      title: t('how_step4_title'),
      description: t('how_step4_desc'),
      details: [
        t('listing.condition'),
        t('guide_search_btn'),
        t('support.hours'),
        t('listing.location')
      ]
    },
    {
      icon: CreditCard,
      title: t('how_step5_title'),
      description: t('how_step5_desc'),
      details: [
        t('listing.contact'),
        t('filters.price'),
        t('common.or'),
        t('support.response_time')
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{content.how_title} | RO Auto Market</title>
        <meta name="description" content={content.how_subtitle} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${content.how_title} | RO Auto Market`} />
        <meta property="og:description" content={content.how_subtitle} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${baseUrl}/og-roautomarket.png`} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@roautomarket" />
        <meta name="twitter:title" content={`${content.how_title} | RO Auto Market`} />
        <meta name="twitter:description" content={content.how_subtitle} />
        <meta name="twitter:image" content={`${baseUrl}/og-roautomarket.png`} />
      </Helmet>
      <SEOHreflang canonicalUrl={canonicalUrl} />

      <main className="container mx-auto px-4 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">
          <div className="surface-dark band-bleed -mt-10 md:-mt-14 py-6 md:py-10 text-center mb-12">
            <nav className="mb-4 text-sm text-muted-foreground">
              <Link to={basePath || '/'} className="hover:text-primary">RO Auto Market</Link>
              <span className="mx-2">›</span>
              <span>{content.how_title}</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent mb-4">
              {content.how_title}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {content.how_subtitle}
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Link to={`${basePath}/cars`} className="text-primary hover:underline text-sm">🚗 {t('how_cta_search')}</Link>
              <span className="text-muted-foreground">•</span>
              <Link to={`${basePath}/pricing`} className="text-primary hover:underline text-sm">💰 {t('how_cta_list')}</Link>
              <span className="text-muted-foreground">•</span>
              <Link to={`${basePath}${country === 'RO' ? '/intrebari-frecvente' : '/faq'}`} className="text-primary hover:underline text-sm">❓ {t('faq_title')}</Link>
            </div>
            <Badge variant="secondary" className="mt-4">
              {t('how_process_time')}
            </Badge>
          </div>

          {/* Steps - Visual Timeline */}
          <div className="relative space-y-8 mb-12">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-[26px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-primary/20"></div>
            
            {steps.map((step, index) => (
              <Card key={index} className="overflow-hidden relative hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-4 text-xl">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-md relative z-10">
                      <step.icon className="text-white" size={26} />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground font-normal">{t('how_step')} {index + 1}</div>
                      <div>{step.title}</div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4 text-base leading-relaxed">
                    {step.description}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {step.details.map((detail, detailIndex) => (
                      <Badge key={detailIndex} variant="secondary" className="justify-start py-2 text-sm">
                        <span className="text-green-600 mr-2">✓</span> {detail}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Benefits Section */}
          <Card className="mb-8 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardHeader>
              <CardTitle className="text-center text-2xl">{t('how_benefits_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <ShieldCheck className="mx-auto text-primary mb-2" size={32} />
                  <p className="font-semibold mb-1">{t('how_benefit1_title')}</p>
                  <p className="text-sm text-muted-foreground">{t('how_benefit1_desc')}</p>
                </div>
                <div>
                  <div className="text-xl font-bold text-primary mb-2">{t('support.hours')}</div>
                  <p className="font-semibold mb-1">{t('how_benefit2_title')}</p>
                  <p className="text-sm text-muted-foreground">{t('how_benefit2_desc')}</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary mb-2">GRATUIT</div>
                  <p className="font-semibold mb-1">{t('how_benefit3_title')}</p>
                  <p className="text-sm text-muted-foreground">{t('how_benefit3_desc')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{t('how_faq_title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-b pb-4">
                <h3 className="font-semibold mb-2">{t('how_faq1_q')}</h3>
                <p className="text-muted-foreground">{t('how_faq1_a')}</p>
              </div>
              <div className="border-b pb-4">
                <h3 className="font-semibold mb-2">{t('how_faq2_q')}</h3>
                <p className="text-muted-foreground">{t('how_faq2_a')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Testimonial CTA */}
          <div className="mb-8">
            <TestimonialCTA variant="full" context="viewer" />
          </div>

          {/* CTA Section */}
          <div className="text-center bg-card/30 rounded-lg p-8 border">
            <h2 className="text-2xl font-semibold mb-4">{content.how_cta}</h2>
            <p className="text-muted-foreground mb-6">
              {t('how_cta_desc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to={`${basePath}/cars`}>{t('how_cta_search')}</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to={`${basePath}/pricing`}>{t('how_cta_list')}</Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HowItWorks;